import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import {
  AlertCircle,
  Copy,
  Loader2,
  Mail,
  Trash2,
  UserPlus,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuth } from '@/lib/auth/useAuth'

interface PendingInvite {
  id: string
  organization_id: string
  email: string
  role: string
  created_at: string
  expires_at: string
  invited_by: string | null
}

interface CreatedInvite {
  id: string
  organization_id: string
  email: string
  role: string
  expires_at: string
  token: string
  accept_url: string
}

const ROLES = ['admin', 'reviewer', 'publisher', 'viewer'] as const

export const Route = createFileRoute('/_app/org/invites')({
  component: InvitesPage,
})

function InvitesPage() {
  const qc = useQueryClient()
  const { currentOrgSlug } = useAuth()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<(typeof ROLES)[number]>('viewer')
  const [formError, setFormError] = useState<string | null>(null)
  const [created, setCreated] = useState<CreatedInvite | null>(null)

  const invites = useOrgQuery<PendingInvite[]>(['admin-invites'], '/invites', {
    refetchOnWindowFocus: true,
  })

  const createInvite = useMutation({
    mutationFn: async () => {
      setFormError(null)
      const r = await apiFetch(orgPath('/invites'), {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), role }),
      })
      if (!r.ok) throw new Error(await r.text())
      return (await r.json()) as CreatedInvite
    },
    onSuccess: (inv) => {
      setEmail('')
      setRole('viewer')
      setCreated(inv)
      qc.invalidateQueries({ queryKey: ['admin-invites', currentOrgSlug] })
    },
    onError: (e: Error) => setFormError(e.message),
  })

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const r = await apiFetch(orgPath(`/invites/${id}`), { method: 'DELETE' })
      if (!r.ok) throw new Error(await r.text())
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-invites', currentOrgSlug] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="p-8 max-w-4xl">
      <PageHeader
        title="Invitations"
        description="Invite people to join this organization. Each invite is tied to a single email address and expires after 24 hours."
      />

      <section className="border border-border rounded-lg p-5 space-y-4 mb-8">
        <div className="flex items-center gap-2">
          <UserPlus className="size-4 text-primary" strokeWidth={1.5} />
          <h2 className="text-sm font-medium tracking-tight">Invite a member</h2>
        </div>

        <div className="grid grid-cols-[1fr,160px,auto] gap-3 items-start">
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              setFormError(null)
            }}
            placeholder="person@example.com"
            autoComplete="off"
          />
          <Select
            value={role}
            onValueChange={(v) => setRole(v as (typeof ROLES)[number])}
          >
            <SelectTrigger aria-label="Invite role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {r}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => createInvite.mutate()}
            disabled={createInvite.isPending || !email.trim()}
            className="h-9"
          >
            {createInvite.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              'Send invite'
            )}
          </Button>
        </div>

        {formError && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-destructive/10 border border-destructive/30">
            <AlertCircle
              className="size-4 text-destructive shrink-0 mt-0.5"
              strokeWidth={1.5}
            />
            <p className="text-xs text-destructive flex-1">{formError}</p>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium tracking-tight">
          Pending invitations
        </h2>

        {invites.isLoading && (
          <p className="text-xs text-muted-foreground font-mono">Loading...</p>
        )}

        {!invites.isLoading && (invites.data?.length ?? 0) === 0 && (
          <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
            No pending invitations.
          </div>
        )}

        {(invites.data?.length ?? 0) > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground bg-secondary/30">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Email</th>
                  <th className="text-left font-medium px-4 py-2.5">Role</th>
                  <th className="text-left font-medium px-4 py-2.5">Sent</th>
                  <th className="text-left font-medium px-4 py-2.5">Expires</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {invites.data!.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-t border-border/40 hover:bg-secondary/20 transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <Mail
                          className="size-3.5 text-muted-foreground"
                          strokeWidth={1.5}
                        />
                        {inv.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{inv.role}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {new Date(inv.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {new Date(inv.expires_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke the invitation for ${inv.email}?`,
                            )
                          ) {
                            revoke.mutate(inv.id)
                          }
                        }}
                        className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="size-3" strokeWidth={1.5} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <CreatedInviteDialog
        invite={created}
        onClose={() => setCreated(null)}
      />
    </div>
  )
}

function CreatedInviteDialog({
  invite,
  onClose,
}: {
  invite: CreatedInvite | null
  onClose: () => void
}) {
  return (
    <Dialog open={!!invite} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invitation created</DialogTitle>
          <DialogDescription>
            An email was sent to{' '}
            <span className="font-mono">{invite?.email}</span>. The link below is
            the same one in the email - copy it now if you need to share it
            out-of-band. We don't store the raw token, so this is the only time
            you'll see it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <CopyField
            label="Invitation link"
            value={invite?.accept_url ?? ''}
          />
          <CopyField label="Token" value={invite?.token ?? ''} mono />
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CopyField({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="space-y-1">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={value}
          className={mono ? 'font-mono text-xs' : 'text-xs'}
        />
        <Button
          variant="outline"
          size="icon"
          onClick={() => {
            navigator.clipboard.writeText(value).then(
              () => toast.success(`${label} copied`),
              () => toast.error('Copy failed'),
            )
          }}
          title={`Copy ${label.toLowerCase()}`}
        >
          <Copy className="size-3.5" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  )
}
